import { Badge, Box, HStack, Icon, Text, Tooltip, useColorModeValue, VStack } from '@elkaso-app/web-design';
import React from 'react';
import { FiAlertTriangle, FiCheckCircle, FiClock, FiHome, FiMapPin, FiPhone } from 'react-icons/fi';

interface EnhancedLicenseCellProps {
  licenseNumber: string;
  businessName: string;
  licenseStatus: string;
  expiryDate?: string;
  legalType?: string;
  isActive: boolean;
  isExpiringSoon: boolean;
  hasCompleteContactInfo: boolean;
  hasCompleteAddress: boolean;
  area?: string;
  activities?: string;
  licenseIssuedAt?: string;
}

export const EnhancedLicenseCell: React.FC<EnhancedLicenseCellProps> = ({
  licenseNumber,
  businessName,
  licenseStatus,
  expiryDate,
  legalType,
  isActive,
  isExpiringSoon,
  hasCompleteContactInfo,
  hasCompleteAddress,
  area,
  activities,
  licenseIssuedAt,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getStatusColor = (status: string, active: boolean) => {
    if (active) return 'green';
    if (status.toLowerCase().includes('expired')) return 'red';
    if (status.toLowerCase().includes('suspended')) return 'yellow';
    return 'gray';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box
      p={4}
      bg={bgColor}
      border='1px solid'
      borderColor={borderColor}
      borderRadius='lg'
      shadow='sm'
      transition='all 0.2s'
      _hover={{ shadow: 'md', borderColor: 'blue.300' }}
      maxW='100%'
      position='relative'>
      {/* License Status Indicator */}
      <Box position='absolute' top={2} right={2}>
        {isActive ? (
          <Icon as={FiCheckCircle} color='green.500' boxSize='16px' />
        ) : (
          <Icon as={FiClock} color='gray.400' boxSize='16px' />
        )}
      </Box>

      <VStack spacing={3} align='start' maxW='100%'>
        {/* Header Section */}
        <VStack spacing={1} align='start' maxW='100%'>
          <HStack spacing={2} maxW='100%'>
            <Tooltip label={licenseNumber}>
              <Text
                fontWeight='bold'
                fontSize='lg'
                color='blue.600'
                lineHeight='1.2'
                maxW='200px'
                wordBreak='break-word'
                whiteSpace='nowrap'
                overflow='hidden'
                textOverflow='ellipsis'>
                {licenseNumber}
              </Text>
            </Tooltip>
            {isExpiringSoon && (
              <Tooltip label='Expires soon!'>
                <Icon as={FiAlertTriangle} color='orange.500' boxSize='16px' />
              </Tooltip>
            )}
          </HStack>

          <Tooltip label={businessName} isDisabled={businessName.length <= 35}>
            <Text
              fontWeight='medium'
              fontSize='md'
              color='gray.800'
              lineHeight='1.2'
              maxW='100%'
              wordBreak='break-word'
              whiteSpace='normal'
              noOfLines={2}>
              {businessName}
            </Text>
          </Tooltip>
        </VStack>

        {/* Status and Legal Type */}
        <HStack spacing={2} flexWrap='wrap'>
          <Badge colorScheme={getStatusColor(licenseStatus, isActive)} variant='subtle' size='sm'>
            {licenseStatus}
          </Badge>
          {legalType && (
            <Badge colorScheme='purple' variant='outline' size='sm'>
              {legalType}
            </Badge>
          )}
        </HStack>

        {/* Quick Info Icons */}
        <HStack spacing={4} flexWrap='wrap'>
          {hasCompleteContactInfo && (
            <Tooltip label='Contact information available'>
              <HStack spacing={1}>
                <Icon as={FiPhone} color='green.500' boxSize='14px' />
                <Text fontSize='xs' color='green.600' fontWeight='medium'>
                  Contact
                </Text>
              </HStack>
            </Tooltip>
          )}

          {hasCompleteAddress && (
            <Tooltip label='Complete address available'>
              <HStack spacing={1}>
                <Icon as={FiMapPin} color='blue.500' boxSize='14px' />
                <Text fontSize='xs' color='blue.600' fontWeight='medium'>
                  Address
                </Text>
              </HStack>
            </Tooltip>
          )}

          {area && (
            <Tooltip label={`Located in ${area}`}>
              <HStack spacing={1}>
                <Icon as={FiHome} color='purple.500' boxSize='14px' />
                <Text fontSize='xs' color='purple.600' fontWeight='medium'>
                  {area}
                </Text>
              </HStack>
            </Tooltip>
          )}
        </HStack>

        {/* Date Information */}
        <VStack spacing={1} align='start' maxW='100%'>
          {expiryDate && (
            <HStack spacing={1}>
              <Icon as={FiClock} color='gray.500' boxSize='12px' />
              <Text fontSize='xs' color='gray.600'>
                Expires: {formatDate(expiryDate)}
              </Text>
            </HStack>
          )}
          {licenseIssuedAt && (
            <HStack spacing={1}>
              <Icon as={FiCheckCircle} color='gray.500' boxSize='12px' />
              <Text fontSize='xs' color='gray.600'>
                Issued: {formatDate(licenseIssuedAt)}
              </Text>
            </HStack>
          )}
        </VStack>

        {/* Activities Preview */}
        {activities && (
          <Box maxW='100%'>
            <Tooltip label={activities}>
              <Text fontSize='xs' color='gray.500' fontStyle='italic' lineHeight='1.2'>
                {truncateText(activities, 60)}
              </Text>
            </Tooltip>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
